'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DiscoveryContentTypeConfig } from '@terra/shared-asset-processing';
import { resolveDiscoveryContentTypes } from '@terra/shared-asset-processing';
import type { CrawlerPeriodicSettingsDto } from '@terra/shared-contracts-applications';
import type { SignalCategoryType } from '@terra/shared-schemas-orchestrator';
import {
  toasterApi,
  type ExecutionsConfigurationCardChange,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../../clients/testerClient';
import {
  APPLICATION_BY_ID_QUERY_KEY,
  useApplicationById,
} from '../../../hooks/api/useApplicationById';
import { DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY } from '../../../hooks/api/useDynamicApplicationScanAutomation';
import { useProjectStore } from '../../../store/projectStore';
import { areDiscoveryContentTypesEqual } from '../areDiscoveryContentTypesEqual';
import {
  toAutomationSettingsRevert,
  toAutomationSettingsValues,
  toExecutionsSettingsUpdate,
  toScheduledReportsValues,
  type AutomationSettingsUpdate,
} from './automation-settings.model';

export function useAutomationSettings() {
  const tDynamicScan = useTranslations(
    'designSystem.automation.dynamicApplicationScan',
  );
  const { selectedTarget, updateSelectedTarget } = useProjectStore();
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const pendingCountRef = useRef(0);
  const values = useMemo(
    () => toAutomationSettingsValues(selectedTarget),
    [selectedTarget],
  );
  const { application: fetchedApplication } = useApplicationById({
    id: selectedTarget?.id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: pendingCount === 0,
  });
  const scheduledReports = useMemo(
    () => toScheduledReportsValues(fetchedApplication),
    [fetchedApplication],
  );

  useEffect(() => {
    if (!fetchedApplication || pendingCountRef.current > 0) {
      return;
    }

    const currentTarget = useProjectStore.getState().selectedTarget;

    if (!currentTarget || currentTarget.id !== fetchedApplication.id) {
      return;
    }

    const fetchedDiscoveryContentTypes =
      fetchedApplication.discoveryContentTypes != null &&
      fetchedApplication.discoveryContentTypes.length > 0
        ? resolveDiscoveryContentTypes(fetchedApplication.discoveryContentTypes)
        : resolveDiscoveryContentTypes(currentTarget.discoveryContentTypes);
    const next = {
      ...currentTarget,
      crawlerPeriodic: fetchedApplication.crawlerPeriodic,
      smokeTestEnabled: fetchedApplication.smokeTestEnabled,
      automationSchedule: fetchedApplication.automationSchedule,
      supportsParallelExecution: fetchedApplication.supportsParallelExecution,
      signalCategoriesInScope: fetchedApplication.signalCategoriesInScope,
      discoveryContentTypes: areDiscoveryContentTypesEqual(
        currentTarget.discoveryContentTypes,
        fetchedDiscoveryContentTypes,
      )
        ? currentTarget.discoveryContentTypes
        : fetchedDiscoveryContentTypes,
    };

    if (
      JSON.stringify(toAutomationSettingsValues(next)) ===
      JSON.stringify(toAutomationSettingsValues(currentTarget))
    ) {
      return;
    }

    updateSelectedTarget(next);
  }, [fetchedApplication, updateSelectedTarget]);

  const persist = useCallback(
    async (update: AutomationSettingsUpdate): Promise<boolean> => {
      const target = useProjectStore.getState().selectedTarget;

      if (!target || Object.keys(update).length === 0) {
        return false;
      }

      const revert = toAutomationSettingsRevert({ previous: target, update });

      updateSelectedTarget({ ...target, ...update });
      pendingCountRef.current += 1;
      setPendingCount(pendingCountRef.current);

      try {
        await terraProxyApiClient.updateApplication({
          id: target.id,
          ...update,
        });
        queryClient.setQueryData(
          [APPLICATION_BY_ID_QUERY_KEY, target.id],
          (current: Record<string, unknown> | undefined) =>
            current ? { ...current, ...update } : current,
        );
        await queryClient.invalidateQueries({
          queryKey: [DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY, target.id],
        });

        return true;
      } catch (error) {
        const latest = useProjectStore.getState().selectedTarget;

        if (latest?.id === target.id) {
          updateSelectedTarget({ ...latest, ...revert });
        }

        toasterApi.error({
          title: tDynamicScan('saveError'),
          description: error instanceof Error ? error.message : undefined,
        });

        return false;
      } finally {
        pendingCountRef.current -= 1;
        setPendingCount(pendingCountRef.current);
      }
    },
    [queryClient, tDynamicScan, updateSelectedTarget],
  );

  const handleExecutionsChange = (change: ExecutionsConfigurationCardChange) =>
    void persist(
      toExecutionsSettingsUpdate({
        change,
        limitExecutionTimeEnabled: values.limitExecutionTimeEnabled,
      }),
    );
  const setCrawlerPeriodic = (crawlerPeriodic: CrawlerPeriodicSettingsDto) =>
    void persist({ crawlerPeriodic });
  const setSignalCategoriesInScope = useCallback(
    (signalCategoriesInScope: SignalCategoryType[]) =>
      persist({ signalCategoriesInScope }),
    [persist],
  );
  const setDiscoveryContentTypes = (
    discoveryContentTypes: DiscoveryContentTypeConfig[],
  ) =>
    void persist({
      discoveryContentTypes: resolveDiscoveryContentTypes(
        discoveryContentTypes,
      ),
    });

  return {
    selectedTarget,
    isSaving: pendingCount > 0,
    ...values,
    scheduledReports,
    nextPentestReportDate: fetchedApplication?.nextPentestReportDate ?? null,
    handleExecutionsChange,
    setCrawlerPeriodic,
    setSignalCategoriesInScope,
    setDiscoveryContentTypes,
  };
}
